import {
  queryOptions,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { FileText } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"
import { z } from "zod/v4"

import { ChangelogTabs } from "@/components/changelog/changelog-tabs"
import { Markdown } from "@/components/markdown"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Form,
  FormCheckbox,
  FormInput,
  FormTextarea,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"
import { useFormState } from "@/hooks/use-form-state"
import { importMarkdown } from "@/http/changelog/entries"
import { listTags } from "@/http/changelog/tags"

const tagsQueryOptions = (params: {
  serviceId: string
  organizationSlug: string
}) =>
  queryOptions({
    queryKey: ["changelog-tags", params.serviceId, params.organizationSlug],
    queryFn: async () => {
      const response = await listTags(params)
      if (response.error) {
        throw new Error(response.error.message)
      }
      return response.data
    },
  })

const importMarkdownSchema = z.object({
  title: z.string().min(1, "Título é obrigatório"),
  markdown: z.string().min(1, "Conteúdo markdown é obrigatório"),
  publishedAt: z.string().optional(),
  isPublished: z.boolean().optional(),
  tagIds: z.array(z.string()).optional(),
})

export const Route = createFileRoute(
  "/_authenticated/$orgSlug/changelog/services/$serviceId/entries/import",
)({
  loader: async ({ context, params }) => {
    await context.queryClient.ensureQueryData(
      tagsQueryOptions({
        serviceId: params.serviceId,
        organizationSlug: params.orgSlug,
      }),
    )
    return {
      title: "Importar Markdown",
      path: `/${params.orgSlug}/changelog/services/${params.serviceId}/entries/import`,
    }
  },
  component: ImportMarkdownPage,
})

function ImportMarkdownPage() {
  const { orgSlug, serviceId } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState("edit")

  const { data: tags } = useSuspenseQuery(
    tagsQueryOptions({
      serviceId,
      organizationSlug: orgSlug,
    }),
  )

  const { state, ...form } = useFormState({
    zodSchema: importMarkdownSchema,
    defaultValues: {
      title: "",
      markdown: "",
      publishedAt: "",
      isPublished: false,
      tagIds: [] as string[],
    },
    action: async (data) => {
      const response = await importMarkdown({
        serviceId,
        organizationSlug: orgSlug,
        title: data.title,
        markdown: data.markdown,
        publishedAt: data.publishedAt || undefined,
        isPublished: data.isPublished,
        tagIds: data.tagIds && data.tagIds.length > 0 ? data.tagIds : undefined,
      })

      if (response.error) {
        return {
          success: false,
          message: response.error.message || "Erro ao importar markdown",
        }
      }

      return { success: true, message: null }
    },
    onSuccess: () => {
      toast.success("Markdown importado com sucesso")
      queryClient.invalidateQueries({
        queryKey: ["changelog-entries", serviceId],
      })
      navigate({
        to: "/$orgSlug/changelog/services/$serviceId/entries",
        params: { orgSlug, serviceId },
      })
    },
    onFailure: (_, error) => {
      toast.error(error || "Erro ao importar markdown")
    },
  })

  const markdownContent = form.watch("markdown")

  const handleTagToggle = (tagId: string, checked: boolean) => {
    const current = form.getValues("tagIds") || []
    if (checked) {
      form.setValue("tagIds", [...current, tagId])
    } else {
      form.setValue(
        "tagIds",
        current.filter((id: string) => id !== tagId),
      )
    }
  }

  const selectedTagIds = form.watch("tagIds") || []

  return (
    <div className="space-y-6">
      <ChangelogTabs
        orgSlug={orgSlug}
        serviceId={serviceId}
        activeTab="entries"
      />
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <FileText className="h-6 w-6" />
            <h1 className="text-2xl font-bold md:text-3xl">
              Importar Markdown
            </h1>
          </div>
          <p className="text-muted-foreground mt-2">
            Importe conteúdo em formato Markdown para criar uma nova entrada no
            changelog.
          </p>
        </div>
      </div>

      <Form {...form} onSubmit={form.onSubmit} className="space-y-6">
        <FormInput name="title" label="Título" required>
          <Input placeholder="Título da entrada" />
        </FormInput>

        <FormInput name="publishedAt" label="Data de publicação">
          <Input type="datetime-local" />
        </FormInput>

        <FormCheckbox name="isPublished" label="Publicar imediatamente">
          <Switch />
        </FormCheckbox>

        {tags && tags.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium">Tags</p>
            <div className="flex flex-wrap gap-3">
              {tags.map((tag) => (
                <label
                  key={tag.id}
                  htmlFor={`tag-${tag.id}`}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <Checkbox
                    id={`tag-${tag.id}`}
                    checked={selectedTagIds.includes(tag.id)}
                    onCheckedChange={(checked) =>
                      handleTagToggle(tag.id, !!checked)
                    }
                  />
                  <span
                    className="inline-flex items-center gap-1.5
                      text-sm"
                  >
                    <span
                      className="h-3 w-3 rounded-full"
                      style={{ backgroundColor: tag.color }}
                    />
                    {tag.name}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="edit">Editar</TabsTrigger>
            <TabsTrigger value="preview">Preview</TabsTrigger>
          </TabsList>

          <TabsContent value="edit">
            <FormTextarea name="markdown" label="Conteúdo Markdown" required>
              <Textarea
                placeholder="Cole ou escreva seu conteúdo em Markdown aqui..."
                className="min-h-[300px] font-mono"
              />
            </FormTextarea>
          </TabsContent>

          <TabsContent value="preview">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Preview</CardTitle>
              </CardHeader>
              <CardContent>
                {markdownContent ? (
                  <div className="prose dark:prose-invert max-w-none">
                    <Markdown>{markdownContent}</Markdown>
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">
                    Nenhum conteúdo para visualizar.
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end">
          <Button type="submit" disabled={state.isSubmitting}>
            {state.isSubmitting ? "Importando..." : "Importar"}
          </Button>
        </div>
      </Form>
    </div>
  )
}
